// Post-processing (pmndrs postprocessing): world pass -> viewmodel pass -> exposure + bloom + tone
// mapping + per-map grade -> FXAA / vignette / film grain. The composer runs on every preset but Low
// (quality.ts `post`): the tone mapping and the grade live in it.

import * as THREE from "three";
import {
  BlendFunction,
  BloomEffect,
  EffectComposer,
  EffectPass,
  FXAAEffect,
  NoiseEffect,
  Pass,
  RenderPass,
  ToneMappingEffect,
  ToneMappingMode,
  VignetteEffect,
} from "postprocessing";
import type { Effect } from "postprocessing";
import type { Settings } from "../settings/schema";
import { ExposureEffect, GradeEffect } from "./gradeEffect";
import type { MapGrade } from "./mapTypes";
import type { QualityPreset } from "./quality";

/** Draws the viewmodel scene into the composer's input buffer on top of the world, depth cleared. */
class ViewmodelPass extends Pass {
  private readonly vmScene: THREE.Scene;
  private readonly vmCamera: THREE.Camera;
  /** Returns false when there is nothing to draw (saves the pass). */
  private readonly shouldDraw: () => boolean;

  constructor(scene: THREE.Scene, camera: THREE.Camera, shouldDraw: () => boolean) {
    super("ViewmodelPass");
    this.needsSwap = false;
    this.vmScene = scene;
    this.vmCamera = camera;
    this.shouldDraw = shouldDraw;
  }

  override render(renderer: THREE.WebGLRenderer, inputBuffer: THREE.WebGLRenderTarget | null): void {
    if (!this.shouldDraw()) return;
    renderer.setRenderTarget(this.renderToScreen ? null : inputBuffer);
    renderer.clearDepth();
    renderer.render(this.vmScene, this.vmCamera);
  }
}

export class PostStack {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly composer: EffectComposer;
  private readonly scene: THREE.Scene;
  private readonly camera: THREE.Camera;
  private readonly vmScene: THREE.Scene;
  private readonly vmCamera: THREE.Camera;
  private readonly shouldDrawViewmodel: () => boolean;
  private key = "";
  private grade: MapGrade;
  private exposure: ExposureEffect | null = null;
  private gradeEffect: GradeEffect | null = null;

  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
    vmScene: THREE.Scene,
    vmCamera: THREE.Camera,
    shouldDrawViewmodel: () => boolean,
    grade: MapGrade,
  ) {
    this.grade = grade;
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;
    this.vmScene = vmScene;
    this.vmCamera = vmCamera;
    this.shouldDrawViewmodel = shouldDrawViewmodel;
    const ext = renderer.extensions;
    const floatTargets = ext.has("EXT_color_buffer_float") || ext.has("EXT_color_buffer_half_float");
    this.composer = new EffectComposer(renderer, {
      depthBuffer: true,
      stencilBuffer: false,
      multisampling: 0,
      frameBufferType: floatTargets ? THREE.HalfFloatType : THREE.UnsignedByteType,
    });
  }

  /** Rebuilds the pass chain when the effect selection or quality changed; true when it did (the caller resizes). */
  configure(video: Settings["video"], q: QualityPreset): boolean {
    const key = `${video.antialias}|${video.bloom}|${video.vignette}|${video.filmGrain}|${q.bloomLevels}|${q.bloomResolutionScale}`;
    if (key === this.key) return false;
    this.key = key;

    // EffectPass.dispose() also disposes its effects.
    for (const pass of this.composer.passes) pass.dispose();
    this.composer.removeAllPasses();

    this.composer.addPass(new RenderPass(this.scene, this.camera));
    this.composer.addPass(new ViewmodelPass(this.vmScene, this.vmCamera, this.shouldDrawViewmodel));

    // Exposure, bloom, tone mapping and the grade share one pass. Bloom stays restrained: only HDR
    // highlights (emissive signs, sun glints) cross the threshold.
    this.exposure = new ExposureEffect(this.grade.exposure);
    this.gradeEffect = new GradeEffect(this.grade);
    const tone: Effect[] = [this.exposure];
    if (video.bloom) {
      tone.push(
        new BloomEffect({
          blendFunction: BlendFunction.ADD,
          luminanceThreshold: 1.05,
          luminanceSmoothing: 0.2,
          mipmapBlur: true,
          intensity: 0.22,
          radius: 0.55,
          levels: q.bloomLevels,
          resolutionScale: q.bloomResolutionScale,
        }),
      );
    }
    tone.push(new ToneMappingEffect({ mode: ToneMappingMode.NEUTRAL }), this.gradeEffect);
    this.composer.addPass(new EffectPass(this.camera, ...tone));

    // FXAA reads the tone-mapped image; vignette and grain go on top so FXAA cannot blur the grain.
    const finish: Effect[] = [];
    if (video.antialias === "fxaa") finish.push(new FXAAEffect());
    if (video.vignette) finish.push(new VignetteEffect({ offset: 0.35, darkness: 0.35 }));
    if (video.filmGrain) {
      const noise = new NoiseEffect({ blendFunction: BlendFunction.OVERLAY, premultiply: true });
      noise.blendMode.opacity.value = 0.05;
      finish.push(noise);
    }
    if (finish.length > 0) this.composer.addPass(new EffectPass(this.camera, ...finish));
    return true;
  }

  /** The buffer the world and viewmodel passes draw into (what programs must be compiled for). */
  get inputBuffer(): THREE.WebGLRenderTarget {
    return this.composer.inputBuffer;
  }

  /** Applies a map's exposure and grade to the live passes (and to the passes built later). */
  setGrade(grade: MapGrade): void {
    this.grade = grade;
    if (this.exposure !== null) this.exposure.exposure = grade.exposure;
    this.gradeEffect?.setGrade(grade);
  }

  /** Call after the renderer's own size changed (CSS size and pixel ratio already applied). */
  setSize(cssWidth: number, cssHeight: number): void {
    this.composer.setSize(cssWidth, cssHeight, false);
  }

  render(dt: number): void {
    this.composer.render(dt);
  }

  dispose(): void {
    this.composer.dispose();
  }
}
